import { computed, ComputedRef } from 'vue'
import addDates from 'date-fns/add'
import formatDate from 'date-fns/format'
import { useIntercom } from '@/lib/intercom/useIntercom'
import { useUser } from '@/composition/user/useUser'
import { useLayouts } from '@/composition/layouts/useLayouts'
import { useLogin } from '@/composition/auth/useLogin'
import { Routes } from '@/router/routes'
import { useRouter } from 'vue-router'
import { loadStripe } from '@stripe/stripe-js'
import { Endpoint, authRequest } from '@/composition/api/useAPI'
import { STRIPE_PUBLISHABLE_KEY } from '@/lib/env'
import { pushToDatalayer } from '@/lib/datalayer'
import { Billing, Api } from '@opteo/types'

function generateTrialEndDate(): string {
    const trialEndDate = addDates(new Date(), { days: Billing.FreeTrialLengthInDays ?? 14 })
    const day = formatDate(trialEndDate, 'do')
    const month = formatDate(trialEndDate, 'MMMM')

    return `${day} of ${month}`
}

interface UseNewPaymentMethod {
    trialEndDate: string
    monthlyFee: ComputedRef<number>
    showNewMessage: (message?: string | undefined) => any
    onSuccess: (paymentMethod: string) => Promise<void>
    logout: () => void
}

export function useNewPaymentMethod(): UseNewPaymentMethod {
    useLayouts('full-screen')
    const trialEndDate = generateTrialEndDate()

    const { showNewMessage } = useIntercom()
    const { currentPlan, userId, mutateUserInfo } = useUser()
    const { logout } = useLogin()
    const router = useRouter()

    const monthlyFee = computed(() => currentPlan.value?.price ?? 0)

    async function onSuccess(paymentMethod: string) {
        const result = await authRequest<Api.UpdateCustomerCc.Response>(Endpoint.UpdateCustomerCc, {
            body: { token: paymentMethod },
        })

        // 3DS validation
        if (result.status === 'requires_action') {
            const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY)
            if (!stripe) throw new Error('Stripe failed to load')
            const { error } = await stripe.confirmCardPayment(result.client_secret, {
                payment_method: paymentMethod,
            })
            if (error) throw new Error(error.message)
        }

        // Only after the card is safely saved as pushBackTrialEnd voids open invoices
        await authRequest(Endpoint.PushBackTrialEnd)

        await mutateUserInfo()
        pushToDatalayer('gtm_added_cc')
        router.push({ name: Routes.AccountCentreInit, params: { id: userId.value } })
    }

    return { trialEndDate, monthlyFee, showNewMessage, onSuccess, logout }
}
